// src/components/settings/shared/IntegrationsSection.jsx
import React from 'react';
import { Link, BarChart3, Mail, Globe, Code, Zap } from 'lucide-react';
import { FormField, TextInput, TextArea, Toggle } from './FormComponents';

export const IntegrationsSection = ({ formData, onInputChange }) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Integrations</h3>
        <p className="text-gray-600">
          Connect with third-party services and tools
        </p>
      </div>

      {/* Analytics */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Analytics</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Google Analytics ID">
              <TextInput
                value={formData.googleAnalyticsId}
                onChange={(value) => onInputChange('googleAnalyticsId', value)}
                placeholder="G-XXXXXXXXXX"
              />
            </FormField>

            <FormField label="Facebook Pixel ID">
              <TextInput
                value={formData.facebookPixelId}
                onChange={(value) => onInputChange('facebookPixelId', value)}
                placeholder="123456789012345"
              />
            </FormField>
          </div>

          <FormField label="Google Tag Manager ID">
            <TextInput
              value={formData.gtmId}
              onChange={(value) => onInputChange('gtmId', value)}
              placeholder="GTM-XXXXXXX"
            />
          </FormField>
        </div>
      </div>

      {/* Email Marketing */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-green-600" />
          <h4 className="text-lg font-semibold text-gray-900">Email Marketing</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Mailchimp API Key">
              <TextInput
                value={formData.mailchimpApiKey}
                onChange={(value) => onInputChange('mailchimpApiKey', value)}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us1"
                type="password"
              />
            </FormField>

            <FormField label="Mailchimp Audience ID">
              <TextInput
                value={formData.mailchimpAudienceId}
                onChange={(value) => onInputChange('mailchimpAudienceId', value)}
                placeholder="xxxxxxxxxx"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="ConvertKit API Key">
              <TextInput
                value={formData.convertkitApiKey}
                onChange={(value) => onInputChange('convertkitApiKey', value)}
                placeholder="Your ConvertKit API key"
                type="password"
              />
            </FormField>

            <FormField label="ConvertKit Form ID">
              <TextInput
                value={formData.convertkitFormId}
                onChange={(value) => onInputChange('convertkitFormId', value)}
                placeholder="123456"
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* CRM Integration */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-purple-600" />
          <h4 className="text-lg font-semibold text-gray-900">CRM & Sales</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="HubSpot API Key">
              <TextInput
                value={formData.hubspotApiKey}
                onChange={(value) => onInputChange('hubspotApiKey', value)}
                placeholder="pat-na1-xxxxxxxx"
                type="password"
              />
            </FormField>

            <FormField label="Salesforce API Key">
              <TextInput
                value={formData.salesforceApiKey}
                onChange={(value) => onInputChange('salesforceApiKey', value)}
                placeholder="Your Salesforce API key"
                type="password"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Pipedrive API Key">
              <TextInput
                value={formData.pipedriveApiKey}
                onChange={(value) => onInputChange('pipedriveApiKey', value)}
                placeholder="Your Pipedrive API key"
                type="password"
              />
            </FormField>

            <FormField label="Zapier Webhook URL">
              <TextInput
                value={formData.zapierWebhook}
                onChange={(value) => onInputChange('zapierWebhook', value)}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Communication */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-600" />
          <h4 className="text-lg font-semibold text-gray-900">Communication</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Slack Webhook URL">
              <TextInput
                value={formData.slackWebhook}
                onChange={(value) => onInputChange('slackWebhook', value)}
                placeholder="https://hooks.slack.com/services/..."
              />
            </FormField>

            <FormField label="Discord Webhook URL">
              <TextInput
                value={formData.discordWebhook}
                onChange={(value) => onInputChange('discordWebhook', value)}
                placeholder="https://discord.com/api/webhooks/..."
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="WhatsApp Business API">
              <TextInput
                value={formData.whatsappApiKey}
                onChange={(value) => onInputChange('whatsappApiKey', value)}
                placeholder="Your WhatsApp API key"
                type="password"
              />
            </FormField>

            <FormField label="SMS Provider API Key">
              <TextInput
                value={formData.smsApiKey}
                onChange={(value) => onInputChange('smsApiKey', value)}
                placeholder="Your SMS provider API key"
                type="password"
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Custom Webhooks */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Code className="h-5 w-5 text-gray-600" />
          <h4 className="text-lg font-semibold text-gray-900">Custom Webhooks</h4>
        </div>
        
        <div className="space-y-4">
          <FormField 
            label="Webhook URLs" 
            description="Enter webhook URLs (one per line) to receive event notifications"
          >
            <TextArea
              value={formData.customWebhooks}
              onChange={(value) => onInputChange('customWebhooks', value)}
              placeholder="https://yoursite.com/webhook1&#10;https://yoursite.com/webhook2"
              rows={4}
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Toggle
              checked={formData.webhookOnBooking}
              onChange={(value) => onInputChange('webhookOnBooking', value)}
              label="Booking Events"
              description="Send webhooks for new bookings"
            />

            <Toggle
              checked={formData.webhookOnPayment}
              onChange={(value) => onInputChange('webhookOnPayment', value)}
              label="Payment Events"
              description="Send webhooks for payments"
            />
          </div>
        </div>
      </div>

      {/* Integration Status */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Integration Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${formData.googleAnalyticsId ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm">Analytics</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${formData.mailchimpApiKey ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm">Email Marketing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${formData.hubspotApiKey || formData.salesforceApiKey ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm">CRM</span>
          </div>
        </div>
      </div>
    </div>
  );
};